import React, { useState, type FC, type PropsWithChildren, useRef, useEffect } from 'react';

import { Box, Container, Heading, Image, Text } from '@lucid/components';
import kebabCase from 'lodash/kebabCase';
import { withImage } from '@lucid/hooks';

import type { Lucid } from 'types/types';
import FlexiCta from '../flexi-cta/flexi-cta';

const listContentCss = (theme: Lucid.Theme.Brand) => ({
  margin: theme.space.s0,
  maxWidth: '333px',
  color: 'inherit',
  [theme.breakpoints.bp900.max]: {
    maxWidth: 'unset',
  },
  [theme.breakpoints.bp500.max]: {
    paddingRight: theme.space.s24,
  },
});

const FlexiInsights: FC<PropsWithChildren<Lucid.Directus.Generated.Flexi_Insights>> = ({ heading, text, items }) => {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const isInsightClicked = useRef(false);
  const thisInsight = useRef(null);
  const chosenArticle = items?.[activeSectionIndex];

  useEffect(() => {
    if (isInsightClicked.current && window.innerWidth <= 500) {
      const current = thisInsight.current as unknown as HTMLElement;

      const activeInsight = current.querySelector('.nineteenInsightsListItem--isActive');

      if (activeInsight) {
        activeInsight.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [activeSectionIndex]);

  const getInsightContent = (
    article: Lucid.Directus.Generated.Flexi_Article,
    isMobile: boolean,
    isActive: boolean,
    isForScreenreaderOnly: boolean = false,
  ) => (
    <Box
      aria-labelledby={kebabCase(article.heading)}
      css={theme => [
        {
          flex: '0 1 55%',
          maxWidth: '55%',
          [theme.breakpoints.bp1220.max]: {
            flex: '0 1 48%',
            maxWidth: '48%',
          },
          [theme.breakpoints.bp900.max]: {
            flex: '0 1 100%',
            maxWidth: '100%',
          },
          [theme.breakpoints.bp500.max]: {
            display: isMobile && isActive ? 'inline-flex' : 'none',
          },
        },
        isMobile &&
          isActive && {
            display: 'inline-flex',
            marginTop: theme.space.s16,
          },
        isMobile && {
          display: 'none',
        },
      ]}
      id={isForScreenreaderOnly ? `${kebabCase(article.heading)}-tab` : undefined}
      role="tabpanel"
    >
      <Box
        css={theme => ({
          marginBottom: theme.space.s32,
          maxWidth: '100%',
          maxHeight: '100%',
          height: 'auto',
          width: 'auto',
          display: 'block',
          [theme.breakpoints.bp500.max]: {
            order: 1,
            margin: theme.space.s0,
          },
        })}
      >
        <Image imageAlt={article?.image?.translations?.[0]?.alt || ''} imageSrc={withImage(article?.image)} />
      </Box>
      <Box
        css={theme => ({
          margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s32}px`,
          [theme.breakpoints.bp900.max]: {
            margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s16}px`,
          },
          [theme.breakpoints.bp500.max]: {
            order: 0,
          },
        })}
      >
        <Box
          dangerouslySetInnerHTML={{
            __html: article.html!,
          }}
        />
        {article.links?.map(({ links_id }, i) => <FlexiCta key={`link-${i}`} data={links_id} />)}
      </Box>
    </Box>
  );

  return (
    <Box
      ref={thisInsight}
      as="section"
      css={theme => ({
        paddingTop: theme.space.s40,
        paddingBottom: theme.space.s40,
        [theme.breakpoints.bp768.max]: {
          paddingTop: theme.space.s48,
          paddingBottom: theme.space.s0,
        },
      })}
    >
      <Container>
        {heading && (
          <Heading as="h2" variant="headingXL">
            {heading}
          </Heading>
        )}
        {text && (
          <Text
            css={theme => ({
              maxWidth: '36rem',
              margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s64}px`,
              [theme.breakpoints.bp900.max]: {
                margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s48}px`,
              },
            })}
            variant="paragraphM400"
          >
            {text}
          </Text>
        )}
        <Box
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <ul
            css={theme => ({
              listStyle: 'none',
              display: 'inline-block',
              margin: theme.space.s0,
              flex: '0 1 40%',
              [theme.breakpoints.bp900.max]: {
                flex: '0 1 100%',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s48}px`,
              },
            })}
            role="tablist"
          >
            {items?.map((article, i) => {
              const isActive = i === activeSectionIndex;

              return (
                <li
                  key={`article-${i}`}
                  className={isActive ? 'nineteenInsightsListItem--isActive' : ''}
                  css={theme => [
                    {
                      borderRadius: theme.radii.r4,
                      background: theme.colors.white,
                      transition: 'border-color 0.25s ease, transform 0.15s ease-in',
                      position: 'relative',
                      marginBottom: theme.space.s16,
                      flex: '0 1 90%',
                      transform: 'translateX(0)',
                      [theme.breakpoints.bp900.max]: {
                        background: 'transparent',
                        flex: '0 1 48%',
                        color: theme.colors.coolGrey[30],
                      },
                      [theme.breakpoints.bp500.max]: {
                        flex: '0 1 100%',
                        color: theme.colors.coolGrey[90],
                      },
                    },
                    isActive && {
                      transform: 'translateX(5%)',
                      [theme.breakpoints.bp900.max]: {
                        color: theme.colors.coolGrey[90],
                      },
                      [theme.breakpoints.bp500.max]: {
                        marginBottom: theme.space.s32,
                        transform: 'none',
                      },
                    },
                  ]}
                  role="presentation"
                >
                  <button
                    aria-controls={`${kebabCase(article?.heading)}-tab`}
                    aria-selected={isActive}
                    css={theme => [
                      {
                        boxShadow: theme.shadows.listItem,
                        width: '100%',
                        borderWidth: `1px 1px 1px ${theme.space.s16}px`,
                        borderStyle: 'solid',
                        borderColor: theme.colors.coolGrey[5],
                        color: theme.colors.coolGrey[70],
                        padding: article.text ? theme.space.s8 : `${theme.space.s16}px ${theme.space.s8}px`,
                        margin: theme.space.s0,
                        textAlign: 'left',
                        background: 'transparent',
                        position: 'relative',
                        [theme.breakpoints.bp500.max]: {
                          padding: theme.space.s24,
                        },
                        '&:hover': {
                          cursor: 'pointer',
                        },
                      },
                      isActive && {
                        borderColor: theme.colors.coolGrey[100],
                        color: theme.colors.coolGrey[100],
                      },
                    ]}
                    onClick={() => {
                      if (window.screen.width <= 500 && isActive) {
                        isInsightClicked.current = false;
                        setActiveSectionIndex(-1);
                      } else {
                        isInsightClicked.current = true;
                        setActiveSectionIndex(i);
                      }
                    }}
                    role="tab"
                    type="button"
                  >
                    <Text css={listContentCss} variant="labelS400">
                      <strong>{article?.heading}</strong>
                    </Text>
                    <Text
                      css={theme => [
                        listContentCss(theme),
                        {
                          [theme.breakpoints.bp900.max]: {
                            display: 'none',
                          },
                          [theme.breakpoints.bp500.max]: {
                            display: 'block',
                          },
                        },
                      ]}
                      variant="paragraphS300"
                    >
                      {article?.text}
                    </Text>
                  </button>
                  {article && getInsightContent(article, true, isActive, true)}
                </li>
              );
            })}
          </ul>
          {chosenArticle && getInsightContent(chosenArticle, false, false)}
        </Box>
      </Container>
    </Box>
  );
};

export default FlexiInsights;
